/**
 * Main Questionnaire Carousel functionality.
 *
 * @module feature/Questionnaire
 */
import CarouselBase from 'CarouselBase';
import AjaxHandler from 'AjaxHandler';

/**
 * Loads Questionnaire data and creates questionnaire carousel functionality.
 *
 * @requires bootstrap/js/dist/questionnaire
 * @requires module:project/Common.AjaxHandler
 * @extends module:project/Common.CarouselBase
 * @memberof module:feature/Questionnaire
 * @version 1.0.0
 * @author Peter van Gennip <peter.van.gennip@valtech.nl>
 */
class Questionnaire extends CarouselBase {
    /**
     * Initializes internal variables, configuration.
     * @param {jQueryElememt} $element DOM Element to be upgraded.
     * @param {object} options Configuration options.
     *
     */
    constructor($element) {
        // update config
        super($element);

        //defaults
        this.config = {
            // base url of api
            apiBase: 'api/feature/questionnaire/getquestions',
            // swipe enabled
            useSwipe: false,
            // skip intro
            skipIntro: false,
        };

        //init ajax helper
        this.ajax = new AjaxHandler({
            type: 'POST',
        });

        this.introSlideTemplate = null;
        this.slideTemplate = null;
        this.pagingTemplate = null;
        this.data = null;
        this.questionSubtitle = null;
        this.$carousel = this.$element.find('.carousel');
        this.$carouselList = this.$element.find('.carousel-inner');
        this.$carouselIndicators = this.$element.find('.carousel-indicators');
        this.$visual = this.$element.find('.visual');
        this.$loader = this.$element.find('.loader');
        this.$introSlideTpl = this.$element.find('.introslidetpl').clone().html();
        this.$slideTpl = this.$element.find('.slidetpl').clone().html();
        this.currentSlideIndex = 1;
        this.totalSlides = null;
        this.outcomesArray = [];
        this.finalOutcome = null;
        this.guid = this.$element.attr('data-guid');
        this.lang = this.$element.attr('data-lang');
        this.response = {};
    }

    /**
     * Initialize.
     */
    init() {
        // show loader
        this.initLoader('fadein');

        // get question data from api endpoint
        this.getData().then(() => {
            this.toNextSlide();

            if (this.data.SkipIntro === true) {
                //Check if SkipIntro flag is set
                this.config.skipIntro = true;
                this.$carouselIndicators.removeClass('is-hidden');
            }

            //Store question subtitle
            this.questionSubtitle = this.data.QuestionSubtitle;

            //Fill visual with src from object
            this.fillVisual(this.data.BackgroundImageUrl);

            //Updates carousel with new slides.
            this.createCarousel(this.data).then((html) => {
                // render slides
                this.$carouselList.html(html);

                // create indicators
                this.generateIndicators();

                if (this.config.skipIntro === true) {
                    // set first question slide to active
                    this.showFirstQuestionSlide();
                }

                super.init(this.$carousel);

                // hide loader
                this.initLoader('fadeout');

                this.getTotalSlides();
            });
        });
    }

    /**
     *initialize loader fadein/fadeout
     * @param {string} fade fadein loader or fadeout loader.
     */
    initLoader(fade) {
        if (fade === 'fadein') {
            this.$loader.fadeIn(150);
        } else {
            this.$loader.fadeOut(150);
        }
    }

    /**
     * gets question data needed to show questions to user
     */
    getData() {
        return new Promise((resolve) => {
            this.ajax
                .request({
                    url: '/' + this.lang + '/' + this.config.apiBase,
                    data: JSON.stringify({ item: this.guid }),
                })
                .then((response) => {
                    // store questiondata
                    this.data = $.extend(this.data, response);

                    resolve(response);
                })
                .catch((error) => {
                    console.error(error);
                });
        });
    }

    /**
     * Show next slide.
     * @param {object} data Json object with question data.
     */
    toNextSlide() {
        $(document).on('click', '.js-questionnaire-next', (e) => {
            e.preventDefault();

            // build string of answers
            this.buildAnswerString(e);

            // Show/hide carousel indicators
            this.toggleIndicators();

            // change index based on intro slide
            if (this.config.skipIntro === false) {
                this.currentSlideIndex = $('.carousel-item.active').index();
            } else {
                this.currentSlideIndex = $('.carousel-item.active').index() + 1;
            }

            // if last slide redirect user else slide to next
            if (this.currentSlideIndex === this.totalSlides) {
                this.redirectUser(this.data);
            } else {
                this.showNext();
            }
        });
    }

    /**
     * Redirect user.
     * @param {object} data Json object with question data.
     */
    redirectUser(data) {
        let redirectUrl = data.Outcomes[this.finalOutcome];

        if (!redirectUrl) {
            redirectUrl = data.DefaultOutcome;
        }

        // hide loader
        this.initLoader('fadein');

        setTimeout(function () {
            window.location = redirectUrl;
        }, 2000);
    }

    /**
     * build answer string out of guids
     */
    buildAnswerString(e) {
        let outcome = this.outcomesArray;
        let $answerBtnDataAttr = $(e.currentTarget).attr('data-answer');

        if ($answerBtnDataAttr !== undefined) {
            outcome.push($answerBtnDataAttr);
        }

        // create complete string
        let completeAnswer = outcome.join(':');
        this.finalOutcome = completeAnswer;
    }

    /**
     * get total of slides
     */
    getTotalSlides() {
        let totalSlides;

        if (this.config.skipIntro === false) {
            totalSlides = this.$carouselList.find('.carousel-item').length - 1;
        } else {
            totalSlides = this.$carouselList.find('.carousel-item').length;
        }

        this.totalSlides = totalSlides;
    }

    /**
     * generates carousel indicators based on number of slides
     */
    generateIndicators() {
        this.$carouselList.find('.carousel-item').each((index) => {
            if (index === 0 && this.config.skipIntro !== true) {
                this.$carouselIndicators.append(
                    '<li data-target="#carouselIndicators" data-slide-to="' + index + '" class="d-none"></li>'
                );
            } else {
                this.$carouselIndicators.append(
                    '<li data-target="#carouselIndicators" data-slide-to="' + index + '"></li>'
                );
            }
        });
        this.$carouselIndicators.find('li:not(.d-none)').first().addClass('active');
    }

    /**
     * toggle indicators based on which indicator is active
     */
    toggleIndicators() {
        if (this.config.skipIntro === false) {
            // if first indicator is active hide indicator container
            if (this.$carouselIndicators.find('li:not(:first)').hasClass('active')) {
                this.$carouselIndicators.addClass('is-active');
            } else {
                this.$carouselIndicators.removeClass('is-active');
            }
        }
    }

    /**
     * set active class to first slide
     */
    showFirstQuestionSlide() {
        this.$carouselList.find('.carousel-item:first').addClass('active');
    }

    /**
     * gets question data needed to show questions to user
     * @param {string} visual src of background visual.
     */
    fillVisual(visual) {
        this.$visual.css('background-image', 'url("' + visual + '")');
    }

    /**
     * Updates carousel with new slides.
     * @param {object} data Json object to render slides in carousel container.
     */
    createCarousel(data) {
        return new Promise((resolve) => {
            let htmlArray = [];
            let promises = [];

            if (this.config.skipIntro === false) {
                let promise = new Promise((resolve) => {
                    this.renderIntro(data.IntroSlide).then((renderedIntro) => {
                        htmlArray.push(renderedIntro);
                        resolve();
                    });
                });

                promises.push(promise);
            }

            //render questions
            for (let question of data.Questions) {
                let promise = new Promise((resolve) => {
                    this.renderQuestion(question).then((renderedQuestion) => {
                        htmlArray.push(renderedQuestion);
                        resolve();
                    });
                });

                promises.push(promise);
            }

            // update list when all items rendered
            Promise.all(promises).then(() => {
                resolve(htmlArray.join(''));
            });
        });
    }

    /**
     * Renders question data to slide template.
     * @param {object} question Json object of item to render.
     */
    renderQuestion(question) {
        return new Promise((resolve) => {
            let slidesHtml = this.$slideTpl;

            // slides
            // FIXME: this is dirty.
            slidesHtml = slidesHtml.replace(/##QUESTIONINTRO##/g, this.questionSubtitle);
            slidesHtml = slidesHtml.replace(/##QUESTIONTITLE##/g, question.Title);
            slidesHtml = slidesHtml.replace(/##ANSWER1##/g, question.Answers[0].Title);
            slidesHtml = slidesHtml.replace(/##DATAANSWER1##/g, question.Answers[0].Guid);
            slidesHtml = slidesHtml.replace(/##ANSWER2##/g, question.Answers[1].Title);
            slidesHtml = slidesHtml.replace(/##DATAANSWER2##/g, question.Answers[1].Guid);
            slidesHtml = slidesHtml.replace(/##ANSWER3##/g, question.Answers[2].Title);
            slidesHtml = slidesHtml.replace(/##DATAANSWER3##/g, question.Answers[2].Guid);

            if (typeof question.Answers[3] !== 'undefined') {
                slidesHtml = slidesHtml.replace(/##ANSWER4##/g, question.Answers[3].Title);
                slidesHtml = slidesHtml.replace(/##DATAANSWER4##/g, question.Answers[3].Guid);
                slidesHtml = slidesHtml.replace(/##ANSWER4CLASS##/g, '');
            } else {
                slidesHtml = slidesHtml.replace(/##ANSWER4##/g, '');
                slidesHtml = slidesHtml.replace(/##DATAANSWER4##/g, '');
                slidesHtml = slidesHtml.replace(/##ANSWER4CLASS##/g, 'd-none');
            }

            if (typeof question.Answers[4] !== 'undefined') {
                slidesHtml = slidesHtml.replace(/##ANSWER5##/g, question.Answers[4].Title);
                slidesHtml = slidesHtml.replace(/##DATAANSWER5##/g, question.Answers[4].Guid);
                slidesHtml = slidesHtml.replace(/##ANSWER5CLASS##/g, '');
            } else {
                slidesHtml = slidesHtml.replace(/##ANSWER5##/g, '');
                slidesHtml = slidesHtml.replace(/##DATAANSWER5##/g, '');
                slidesHtml = slidesHtml.replace(/##ANSWER5CLASS##/g, 'd-none');
            }

            resolve(slidesHtml);
        });
    }

    /**
     * Renders intro data to intro slide template.
     * @param {object} intro Json object of item to render.
     */
    renderIntro(data) {
        return new Promise((resolve) => {
            let introSlideHtml = this.$introSlideTpl;

            // intro slide
            introSlideHtml = introSlideHtml.replace(/##QUESTIONINTRO##/g, data.Introduction);
            introSlideHtml = introSlideHtml.replace(/##QUESTIONTITLE##/g, data.Title);
            introSlideHtml = introSlideHtml.replace(/##QUESTIONSUBTITLE##/g, data.Subtitle);
            introSlideHtml = introSlideHtml.replace(/##QUESTIONBTN##/g, data.StartButtonText);
            resolve(introSlideHtml);
        });
    }
}

// register to Component Handler
window.ComponentHandler.register({
    constructor: Questionnaire,
    classAsString: 'Questionnaire',
    cssClass: 'js-questionnaire',
});

export default Questionnaire;
